import React, { useState, useEffect, useContext, useCallback } from "react";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import axiosInstance from "../utils/axiosInstance";
import { API_URL } from "../utils/runtimeConfig";
import Modal from "react-modal";
import AddNationModal from "../modals/AddNationModal";
import "./css/Nations.css";
import { AuthContext } from "../context/AuthContext";
import { AppButton, DataTableShell, EmptyState, PageShell } from "../components/ui";

Modal.setAppElement("#root");

const apiUrl = API_URL;

const getConsumedNations = (nation) => (
  Array.isArray(nation?.consumedNations)
    ? nation.consumedNations
    : (Array.isArray(nation?.absorbedNation)
      ? nation.absorbedNation.map((number) => ({ number }))
      : [])
);

const Nations = () => {
  const axios = axiosInstance;
  const [nations, setNations] = useState([]);
  const { user } = useContext(AuthContext);
  const [isAddNationModalOpen, setIsAddNationModalOpen] = useState(false);

  const fetchNations = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/nations`);
      setNations(response.data || []);
    } catch (error) {
      console.error("Error fetching nations:", error);
    }
  }, [axios]);

  useEffect(() => {
    fetchNations();
  }, [fetchNations]);

  const isSuperUser = () => user?.role === "superUser";
  const consumedNationTotal = nations.reduce(
    (total, nation) => total + getConsumedNations(nation).length,
    0
  );

  return (
    <PageShell maxWidth="lg">
      <div className="nations-page">
        <DataTableShell
          title="Nations"
          subtitle={`${nations.length} active${consumedNationTotal ? `, ${consumedNationTotal} consumed` : ""}`}
          actions={
            isSuperUser() ? (
              <AppButton onClick={() => setIsAddNationModalOpen(true)}>Add Nation</AppButton>
            ) : null
          }
        >
          {nations.length === 0 ? (
            <EmptyState title="No nations found" description="Nations will appear here once created." />
          ) : (
            <div className="nations-table-wrap">
              <Table size="small" className="nations-table" aria-label="List of available nations">
                <TableHead>
                  <TableRow>
                    <TableCell>Nation Number</TableCell>
                    <TableCell>Consumed Nations</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nations.map((nation) => {
                    const consumedNations = getConsumedNations(nation);
                    return (
                      <TableRow key={nation._id}>
                        <TableCell className="nations-number-cell">{nation.number}</TableCell>
                        <TableCell>
                          {consumedNations.length ? (
                            <div
                              className="nations-consumed-list"
                              aria-label={`Nation ${nation.number} consumed ${consumedNations.length} nations`}
                            >
                              {consumedNations.map((consumedNation) => (
                                <span
                                  key={consumedNation._id || consumedNation.number}
                                  className="nations-consumed-chip"
                                  title={consumedNation.mergedAt ? `Merged ${new Date(consumedNation.mergedAt).toLocaleDateString()}` : undefined}
                                >
                                  {consumedNation.number}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="nations-muted">None</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </DataTableShell>

        {isAddNationModalOpen && (
          <AddNationModal
            isOpen={isAddNationModalOpen}
            onRequestClose={() => setIsAddNationModalOpen(false)}
            onNationAdded={fetchNations}
          />
        )}
      </div>
    </PageShell>
  );
};

export default Nations;
